/**
 * @fileoverview Auditor Dashboard page component
 * Displays auditor-specific dashboard with statistics and quick access buttons
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, HTTP_STATUS, intl } from "dmsbasereact";

// Icons
import DescriptionIcon from "@mui/icons-material/Description";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TimelineIcon from "@mui/icons-material/Timeline";

// Components
import { DashboardLayout } from "../../components/Dashboard";

// Utils and Theme
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";

/**
 * AuditorDashboard - Main dashboard page for auditors
 *
 * Features:
 * - Stats cards showing prescription and dispensation totals
 * - Quick access buttons to traceability and sales reports
 *
 * @returns {JSX.Element} The auditor dashboard page
 */
export const AuditorDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    /**
     * Fetch dashboard stats from the API
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const statsResponse = await fetchData("/dashboard/auditor/stats", "GET", true);

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                setStats(statsResponse.data?.data);
            }
        } catch (err) {
            console.error("Error fetching dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Configure stats cards
    const statsConfig = [
        {
            title: intl.get("TotalPrescriptions").d("Total Prescriptions"),
            value: stats?.total_prescriptions || 0,
            icon: DescriptionIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.ReportsPrescriptions)
        },
        {
            title: intl.get("TotalDispensations").d("Total Dispensations"),
            value: stats?.total_dispensations || 0,
            icon: VaccinesIcon,
            color: APP_COLORS.system.success,
            onClick: () => navigate(APP_ROUTES.ReportsDispensations)
        }
    ];

    // Configure quick access buttons
    const quickAccessConfig = [
        {
            label: intl.get("Analytics.Menu.Traceability").d("Traceability"),
            icon: TimelineIcon,
            route: APP_ROUTES.AnalyticsTraceability,
            color: APP_COLORS.system.success
        },
        {
            label: intl.get("Analytics.Menu.Sales").d("Sales"),
            icon: TrendingUpIcon,
            route: APP_ROUTES.AnalyticsSales,
            color: APP_COLORS.system.info
        }
    ];

    return (
        <DashboardLayout
            welcomeName={stats?.auditor_name}
            loading={loading}
            stats={statsConfig}
            quickAccess={quickAccessConfig}
            alerts={[]}
        />
    );
};
